import {Parameter, Parameters, Flags, BaseFlagProps} from 'platform/components';
import {
  Box,
  HStack,
  VStack,
  Image,
  Icon,
  Text,
  Clickable,
  Hide,
  Show,
  useDevice,
  Device,
} from 'platform/foundation';
import {currencies, formatCurrencySymbol, useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {DashboardVehicleResponseBody} from '@dms/api/core';
import {SaleVehicleBuyingState, SaleVehicleSellingState} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  getVehicleAdvertisementTypeFlagProps,
  getVehicleBuyingStateFlagProps,
  getVehiclePhotographedFlagProps,
  getVehicleSaleTypeFlagProps,
  getVehicleSellingStateFlagProps,
  useIsLicenceEnabled,
  usePermissions,
  useTenant,
  useVehiclePhotosPermissions,
} from '@dms/shared';

import {
  CurrencyCodeType,
  EMPTY_PLACEHOLDER,
  TestIdProps,
  buildArray,
  composePath,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {usePriceFormatter} from '../../../hooks/usePriceFormatter';
import {convertTagToFlag} from '../../../utils/convertTagToFlag';
import {HoverableBackground} from '../../HoverableBackground';

interface LatestVehiclesProps extends TestIdProps {
  vehicle: DashboardVehicleResponseBody;
  isProbe?: boolean;
  isPreview?: boolean;
}

export const LatestVehicleItem = (props: LatestVehiclesProps) => {
  const isInert = props.isProbe || props.isPreview;
  const navigate = useNavigate();
  const {formatPrice} = usePriceFormatter();
  const {tenantCurrency} = useTenant();
  const formatCurrency = useFormatCurrency();
  const device = useDevice();

  const [canReadBusinessCase] = usePermissions({permissionKeys: ['businessCaseRead']});
  const {isAdvertisingEnabled} = useIsLicenceEnabled();
  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: props.vehicle.id,
    shouldSkip: isInert,
  });

  const onClickOpenVehicleDetail = () => {
    navigate(composePath(vehiclesRoutes.detail, {params: {id: props.vehicle.id}}));
  };

  const flags = generateVehicleFlags(
    props.vehicle,
    device,
    canReadBusinessCase,
    canReadVehicleAlbumPhotos,
    isAdvertisingEnabled
  );
  const imgUrlWithHeight = `${props.vehicle?.imageUrl}&height=64`;

  return (
    <Clickable isDisabled={isInert} onClick={isInert ? undefined : onClickOpenVehicleDetail}>
      <HoverableBackground>
        <HStack spacing={[4, 2, 4]} minWidth={[50, 50, 100]}>
          <HStack width={24}>
            <Box
              width={24}
              height={16}
              backgroundColor={
                props.isProbe || !canReadVehicleAlbumPhotos || !props.vehicle.imageUrl
                  ? 'palettes.neutral.30.100'
                  : undefined
              }
              borderRadius="small"
              overflow="hidden"
            >
              <Show
                when={
                  props.isProbe !== true &&
                  canReadVehicleAlbumPhotos &&
                  isNotNilOrEmpty(props.vehicle.imageUrl)
                }
              >
                <Show onNotebook onTablet onDesktop>
                  <Box borderRadius="small" overflow="hidden">
                    <Image
                      fit="contain"
                      ratio="3 / 2"
                      src={imgUrlWithHeight}
                      data-testid={suffixTestId('latest-vehicle-item-img', props)}
                    />
                  </Box>
                </Show>

                <Show onMobile>
                  <Box
                    backgroundColor="palettes.neutral.30.100"
                    borderRadius="small"
                    overflow="hidden"
                    flex={1}
                  >
                    <Image
                      fit="contain"
                      ratio="3 / 2"
                      src={imgUrlWithHeight}
                      data-testid={suffixTestId('latest-vehicle-item-img', props)}
                    />
                  </Box>
                </Show>
              </Show>
              <Hide
                when={
                  props.isProbe !== true &&
                  canReadVehicleAlbumPhotos &&
                  isNotNilOrEmpty(props.vehicle.imageUrl)
                }
              >
                <HStack height="100%" width="100%" align="center" justify="center">
                  <Icon value="maps/directions_car" size={10} color="palettes.neutral.800.100" />
                </HStack>
              </Hide>
            </Box>
          </HStack>
          <Box flex={3}>
            <VStack spacing={1} grow={1}>
              <HStack spacing={1}>
                <Flags
                  spacing={1}
                  flags={flags}
                  shouldWrap
                  data-testid={suffixTestId('latest-vehicle-item-flags', props)}
                />
              </HStack>
              <Text size={['xSmall', 'base']} alternative>
                {props.vehicle.title}
              </Text>
              <Parameters
                size="xSmall"
                parameters={getVehicleParams(props.vehicle)}
                data-testid={suffixTestId('latest-vehicle-item-parameters', props)}
              />
            </VStack>
          </Box>
          <Box flex={2} paddingTop={4}>
            <VStack align="flex-end" justify="center" spacing={1}>
              <Show when={isNotNilOrEmpty(props.vehicle.priceWithVat)}>
                <Text size={['xSmall', 'base']} alternative>
                  {formatPrice(props.vehicle.priceWithVat)}
                </Text>
              </Show>
              <Hide when={isNotNilOrEmpty(props.vehicle.priceWithVat)}>
                <Text size={['xSmall', 'base']}>
                  {`${EMPTY_PLACEHOLDER} ${formatCurrencySymbol(currencies, (props.vehicle?.priceWithVat?.currency as CurrencyCodeType) ?? tenantCurrency)}`}
                </Text>
              </Hide>
              <Show when={props.vehicle.isVatDeductible}>
                <Show when={isNotNilOrEmpty(props.vehicle?.priceWithoutVat?.amount)}>
                  <Text size="xSmall" color="secondary">
                    {`${formatCurrency(
                      Number(props.vehicle?.priceWithoutVat?.amount),
                      props.vehicle?.priceWithoutVat?.currency ?? tenantCurrency
                    )} ${i18n.t('general.labels.withoutVat')}`}
                  </Text>
                </Show>
                <Hide when={isNotNilOrEmpty(props.vehicle?.priceWithoutVat?.amount)}>
                  <Text size="xSmall" color="secondary">
                    {`${EMPTY_PLACEHOLDER} ${formatCurrencySymbol(currencies, (props.vehicle?.priceWithoutVat?.currency as CurrencyCodeType) ?? tenantCurrency)} ${i18n.t('general.labels.withoutVat')}`}
                  </Text>
                </Hide>
              </Show>
              <Hide when={props.vehicle.isVatDeductible}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.vehicle.labels.nondeductible')}
                </Text>
              </Hide>
            </VStack>
          </Box>
        </HStack>
      </HoverableBackground>
    </Clickable>
  );
};

function generateVehicleFlags(
  vehicle: DashboardVehicleResponseBody,
  device: Device,
  canReadBusinessCase: boolean,
  canReadVehicleAlbumPhotos: boolean,
  isAdvertisingEnabled: boolean
): BaseFlagProps[] {
  const isSalesModuleEnabled = canReadBusinessCase;

  const saleTypeFlagProps = getVehicleSaleTypeFlagProps(vehicle.saleType);
  const sellingStateFlagProps = getVehicleSellingStateFlagProps(
    vehicle.sellingState as SaleVehicleSellingState
  );
  const buyingStateFlagProps = getVehicleBuyingStateFlagProps(
    vehicle.buyingState as SaleVehicleBuyingState
  );
  const advertisementStateFlagProps = getVehicleAdvertisementTypeFlagProps(vehicle.isAdvertised);
  const photographedFlagProps = getVehiclePhotographedFlagProps(vehicle.isPhotographed);

  const shouldDisplaySellingStateFlag = isSalesModuleEnabled && isNotNil(sellingStateFlagProps);
  const shouldDisplayBuyingStateFlag = isSalesModuleEnabled && isNotNil(buyingStateFlagProps);
  const shouldDisplayAdvertisementFlag =
    isSalesModuleEnabled &&
    isAdvertisingEnabled &&
    (sellingStateFlagProps || buyingStateFlagProps) &&
    isNotNil(advertisementStateFlagProps);
  const shouldDisplayPhotographedFlag =
    isSalesModuleEnabled && canReadVehicleAlbumPhotos && isNotNil(photographedFlagProps);
  const shouldDisplayIsReservedFlag = isSalesModuleEnabled && vehicle.isReserved;

  const flags = buildArray<BaseFlagProps>()
    .when(shouldDisplayIsReservedFlag, {
      label: i18n.t('entity.vehicle.labels.reserved'),
      colorScheme: 'yellow',
    })
    .when(vehicle.isArchived, {
      label: i18n.t('entity.vehicle.labels.archived'),
      colorScheme: 'neutral',
    })
    .when(
      isNotNil(saleTypeFlagProps) && !vehicle?.isArchived && isSalesModuleEnabled,
      saleTypeFlagProps!
    )
    .when(shouldDisplaySellingStateFlag, sellingStateFlagProps!)
    .when(shouldDisplayBuyingStateFlag, buyingStateFlagProps!)
    .when(shouldDisplayAdvertisementFlag, advertisementStateFlagProps!)
    .when(shouldDisplayPhotographedFlag, photographedFlagProps!);

  flags.forEach((flag) => (flag.size = 'small'));

  vehicle.tags.forEach((tag) => flags.add(convertTagToFlag(tag)));

  if (device === 'mobile' || device === 'tablet') {
    return flags.splice(0, 2);
  }

  return flags;
}

function getVehicleParams(vehicle: DashboardVehicleResponseBody) {
  const {manufacturedOn, mileage, fuelType, transmission} = vehicle;
  return buildArray<Parameter>()
    .when(manufacturedOn, {
      title: manufacturedOn,
      tooltip: i18n.t('entity.vehicle.labels.manufactureYear'),
    })
    .when(mileage, {
      title: mileage,
      tooltip: i18n.t('entity.vehicle.labels.realMileage'),
    })
    .when(fuelType, {
      title: fuelType,
      tooltip: i18n.t('entity.vehicle.labels.fuelType'),
    })
    .when(transmission, {
      title: transmission,
      tooltip: i18n.t('entity.vehicle.labels.transmission'),
    });
}
